import React, { useState } from 'react';
import { MainLayout } from '../../../../components/MainLayout';
import { PagesHeader } from '../../../../components/shared/PagesHeader';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { COLUMNS, ROUTES, SEARCH_CONFIG } from './SpecialBillingList.config';
import DataTable from '../../../../components/shared/DataTable/DataTable';
import styles from './SpecialBillingList.styles.scss';
import cn from 'classnames';
import DeleteBillingAgreementDialog from '../../components/DeleteBillingAgreementDialog/DeleteBillingAgreementDialog';
import TerminateBillingAgreementDialog from '../../components/TerminateBillingAgreementDialog/TerminateBillingAgreementDialog';
import CreateBillingAgreementAsDialog from '../../components/CreateBillingAgreementAsDialog/CreateBillingAgreementAsDialog';
import { AGREEMENT_TYPE } from '../../config/constants';
import { useAuthentication } from '../../../../hooks/useAuthentication';
import { PERMISSIONS } from '../../../../helpers/auth';

const SpecialBillingList = ({
  renderedAgreements,
  originalAgreements,
  setSpecialBillingList,
  tableLoading,
  confirmDeleteAgreement,
  confirmTerminateAgreements,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userInfo, hasRole } = useAuthentication();

  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteRow, setDeleteRow] = useState(false);
  const [terminateAgreements, setTerminateAgreements] = useState(false);
  const [showCreateAgreementAs, setShowCreateAgreementAs] = useState(false);
  const [agreementType, setAgreementType] = useState(AGREEMENT_TYPE.SELF);

  const deleteAgreement = (rowData) => {
    setDeleteRow(rowData);
  };

  const isAgreementsTerminatable = (agreement) => {
    return (
      agreement.status === 'Active' ||
      (agreement.status === 'Inactive' &&
        agreement.reason === 'Future_Dated_And_Approved')
    );
  };

  const isDataSelectable = (agreement) =>
    isAgreementsTerminatable(agreement.data);

  const cellClassName = (data, options) =>
    options.field === 'field_0' &&
    !isAgreementsTerminatable(renderedAgreements[options.rowIndex])
      ? 'p-disabled'
      : '';

  return (
    <MainLayout eInvoicing={true}>
      <PagesHeader
        withBreadcrumb={true}
        title={t('special-billing-agreements')}
        routes={ROUTES}
        wrapperClassName={'mb-20'}
        withActionButton={true}
        action={
          <div className="d-flex gap-3">
            {hasRole(PERMISSIONS.SUBMIT_AGREEMENT) && (
              <Button
                className="px-4"
                label={t('special_billing.add-self-billing-agreement')}
                onClick={() => {
                  setAgreementType(AGREEMENT_TYPE.SELF);
                  setShowCreateAgreementAs(true);
                }}
              />
            )}
            {hasRole(PERMISSIONS.SUBMIT_AGREEMENT) && (
              <Button
                className="px-4"
                label={t('special_billing.add-third-party-billing-agreement')}
                onClick={() => {
                  setAgreementType(AGREEMENT_TYPE.THIRD);
                  setShowCreateAgreementAs(true);
                }}
              />
            )}
          </div>
        }
      />

      <div className={cn(styles.failureListTable, 'mt-3')}>
        <DataTable
          isDataSelectable={isDataSelectable}
          cellClassName={cellClassName}
          selectable
          removableSort
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          loading={tableLoading}
          value={renderedAgreements}
          filterDisplay="menu"
          showSortInFilters={true}
          searchConfig={SEARCH_CONFIG}
          columns={() =>
            COLUMNS({ t, navigate, deleteAgreement, user: userInfo })
          }
          dataKey="agreementId"
          searchFields={[
            'referenceNumber',
            'sellerName',
            'vatAccountOrTIN',
            'buyerOrThirdPartyName',
            'buyerOrThirdPartyVAT',
          ]}
        />
      </div>
      <div className="d-flex gap-3 flex-row-reverse">
        <Button
          disabled={selectedRows.length === 0}
          className="px-4"
          label={t('special_billing.terminate-agreement-s')}
          onClick={() => {
            setTerminateAgreements(selectedRows);
          }}
        />
      </div>
      <DeleteBillingAgreementDialog
        deleteRow={deleteRow}
        setDeleteRow={setDeleteRow}
        confirmDeleteAgreement={confirmDeleteAgreement}
      />
      <TerminateBillingAgreementDialog
        terminateAgreements={terminateAgreements}
        setTerminateAgreements={setTerminateAgreements}
        confirmTerminateAgreements={confirmTerminateAgreements}
      />
      <CreateBillingAgreementAsDialog
        visible={showCreateAgreementAs}
        setVisible={setShowCreateAgreementAs}
        agreementType={agreementType}
        onConfirm={(selectedOption) => {
          navigate('./add', {
            state: {
              createAs: selectedOption,
              agreementType: agreementType,
            },
          });
        }}
      />
    </MainLayout>
  );
};

export default SpecialBillingList;
